import React from 'react';
import { Grid } from '@mui/material';

import { useSettings } from 'app/main/components';
import { ModuleSearchField, NumberTextField, SearchSelectField, SimpleTextField } from 'app/main/components/fields';

import { type OrdersLinesEditDialogSection } from '../OrdersLinesEditDialog';
import { getOrderQuantities } from '../../components/OrdersLinesTableUtils';
import QuantitySelectField from '../../components/QuantitySelectField';

type OrdersLinesEditDialogMainSectionProps = OrdersLinesEditDialogSection & {
	handleItemChange: (newItem: any) => void;
	isItem?: boolean;
	isWorkReportsHours?: boolean;
};

export default function OrdersLinesEditDialogMainSection(props: OrdersLinesEditDialogMainSectionProps) {
	const { sysSettings, t } = useSettings();

	const isLinesStatusEnabled = sysSettings.orders_lines_status_enabled === 'true';

	const quantities = getOrderQuantities(props.model.item_ords_multiple, props.model.item_ords_qty_min);

	return (
		<>
			{/* ITEM */}
			{props.isItem && (
				<Grid size={12}>
					<ModuleSearchField
						name="orli_item_uid"
						label={t('DETAILS_ORDERS_LINES_FIELD_ITEM')}
						module="items"
						uid={props.model.orli_item_uid}
						filters={[{ filt_field: 'item_blocked', filt_clause: 'not_equal', filt_value: 1 }]}
						isEdit={props.canEdit}
						sendChange={props.sendChange}
						afterChange={props.handleItemChange}
						required
						validationError={t('ERROR_TEXT_REQUIRED')}
					/>
				</Grid>
			)}
			{/* WORKREPORT TYPE */}
			{props.isWorkReportsHours && (
				<Grid size={12}>
					<SearchSelectField
						name="orli_woty_id"
						label={t('DETAILS_ORDERS_LINES_FIELD_SERVICE_TYPE')}
						systable="workreports_types"
						value={props.model.orli_woty_id}
						isEdit={props.canEdit}
						sendChange={props.sendChange}
						required
						validationError={t('ERROR_TEXT_REQUIRED')}
					/>
				</Grid>
			)}
			{/* DESCRIPTION */}
			<Grid size={12}>
				<SimpleTextField
					name="orli_description"
					label={t('DETAILS_ORDERS_LINES_FIELD_DESCRIPTION')}
					value={props.model.orli_description}
					isEdit={props.canEdit}
					sendChange={props.sendChange}
					required
					validations={{ maxLength: 100 }}
					validationErrors={{
						isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED'),
						maxLength: t('ERROR_TEXT_MAXLENGTH')
					}}
				/>
			</Grid>
			{/* STATUS */}
			{(props.isItem || props.isWorkReportsHours) && isLinesStatusEnabled && (
				<Grid size={{ sm: 6, xs: 12 }}>
					<SearchSelectField
						name="orli_orst_id"
						label={t('DETAILS_ORDERS_LINES_FIELD_ORDER_STATUS')}
						systable="orders_statuses"
						value={props.model.orli_orst_id}
						isEdit={props.canEdit}
						sendChange={props.sendChange}
						required
						validationError={t('ERROR_TEXT_REQUIRED')}
					/>
				</Grid>
			)}
			{/* QUANTITY */}
			{(props.isItem || props.isWorkReportsHours) && (
				<Grid size={{ sm: 6, xs: 12 }}>
					{!props.canEdit ? (
						<QuantitySelectField
							quantities={quantities}
							value={props.model.orli_quantity}
							pricingLots={props.model.pricings_lots}
							discountPercentage={props.model.orli_discount_final}
							handleChange={props.sendChange}
						/>
					) : (
						<NumberTextField
							name="orli_quantity"
							label={t('DETAILS_ORDERS_LINES_FIELD_QUANTITY')}
							value={props.model.orli_quantity}
							isEdit
							sendChange={props.sendChange}
							required
							validationError={t('ERROR_TEXT_REQUIRED')}
						/>
					)}
				</Grid>
			)}
			{/* ORDER */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<NumberTextField
					name="orli_order"
					label={t('DETAILS_ORDERS_LINES_FIELD_ORDER')}
					value={props.model.orli_order}
					isEdit={props.canEdit}
					sendChange={props.sendChange}
				/>
			</Grid>
		</>
	);
}
